import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
    AgentSelection,
    BenchmarkRequestError, MappingRelation, SamplePrerequisites, SupplyPreview, SuppliedSampleData, benchmarkService
} from '../../../services/benchmarkService';
import { validateFlowCode } from '../benchmarkFlowCodes';
import {
    ACCEPTED_EXTENSIONS, ParsedSample, SampleCheck, SampleParseError, checkAlertSample, checkKycSample, parseSampleFile
} from './sampleFile';
import { followContextStream, loaderOutcome } from './contextStream';
import { draftRelation, refusalGuidance } from './refusalGuidance';
import SamplePreview from './SamplePreview';
import ConfirmColumns from './ConfirmColumns';
import AgentSelectionPicker from '../AgentSelectionPicker';
import { dsVar, ink } from '../../../theme/tokens';

/** How long to wait for the loader's report before saying the stream is not reaching us. */
const PREVIEW_WAIT_MS = 90_000;
const PREVIEW_ROWS = 50;

type Stage = 'upload' | 'previewing' | 'preview' | 'refused' | 'confirm' | 'starting';

interface LoadedFile {
    sample: ParsedSample | null;
    error: string | null;
}

const EMPTY_FILE: LoadedFile = { sample: null, error: null };

const newInteractionId = () =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
        : Math.random().toString(36).slice(2, 18);

interface SuppliedSampleWizardProps {
    open: boolean;
    onClose: () => void;
    /** The workflow being benchmarked; the sample runs under VALIDATE_ + this code. */
    workflowCode: string;
    projectId: string;
    /**
     * The full run has been accepted. The editor follows it from here, on the
     * same live timeline a backtest uses.
     */
    onRunStarted: (run: { interactionId: string; referenceId: string | null }) => void;
}

/**
 * Benchmark a sample of alerts the user supplies: upload, preview how it was
 * read, run, and hand the run to the editor to follow.
 *
 * The preview is the point of the flow. The loader reads the file exactly as
 * the run will, and reports the column mapping, the tiers as the platform
 * reads them, the evidence available and anything it noticed — so a user sees
 * how their file was understood before committing to a run that takes minutes.
 */
const SuppliedSampleWizard: React.FC<SuppliedSampleWizardProps> = ({
    open, onClose, workflowCode, projectId, onRunStarted
}) => {
    const [stage, setStage] = useState<Stage>('upload');
    const [alerts, setAlerts] = useState<LoadedFile>(EMPTY_FILE);
    const [kyc, setKyc] = useState<LoadedFile>(EMPTY_FILE);
    const [prerequisites, setPrerequisites] = useState<SamplePrerequisites | null>(null);
    const [prerequisitesError, setPrerequisitesError] = useState<string | null>(null);
    const [preview, setPreview] = useState<SupplyPreview | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirmRelation, setConfirmRelation] = useState<MappingRelation>('case_history_alert');
    // The agents the replay runs; null runs every agent the workflow has.
    const [agentSelection, setAgentSelection] = useState<AgentSelection | null>(null);
    const streamRef = useRef<AbortController | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stopFollowing = useCallback(() => {
        streamRef.current?.abort();
        streamRef.current = null;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    // A fresh wizard each time it opens; nothing follows a closed one.
    useEffect(() => {
        if (!open) {
            stopFollowing();
            return;
        }
        setStage('upload');
        setAlerts(EMPTY_FILE);
        setKyc(EMPTY_FILE);
        setPreview(null);
        setRefusal(null);
        setError(null);
        setPrerequisitesError(null);
        benchmarkService.getSamplePrerequisites()
            .then(setPrerequisites)
            .catch((err) => setPrerequisitesError(
                err instanceof BenchmarkRequestError ? err.message : 'Could not check whether your organisation is set up for this.'
            ));
    }, [open, stopFollowing]);

    useEffect(() => stopFollowing, [stopFollowing]);

    const loadFile = async (file: File | undefined, kind: 'alerts' | 'kyc') => {
        const set = kind === 'alerts' ? setAlerts : setKyc;
        if (!file) {
            set(EMPTY_FILE);
            return;
        }
        try {
            const sample = await parseSampleFile(file, kind);
            set({ sample, error: null });
        } catch (err) {
            set({ sample: null, error: err instanceof SampleParseError ? err.message : 'The file could not be read.' });
        }
    };

    // The checks depend on both files: KYC is matched against the alerts' customers.
    const alertCheck = alerts.sample ? checkAlertSample(alerts.sample) : null;
    const kycCheck = kyc.sample ? checkKycSample(kyc.sample, alerts.sample) : null;
    const blocked = !alerts.sample
        || Boolean(alerts.error || kyc.error)
        || Boolean(alertCheck?.blocks.length || kycCheck?.blocks.length)
        || prerequisites?.tierMapping.approved === false;

    const payload = (previewOnly: boolean): SuppliedSampleData => ({
        projectId,
        alertRecords: alerts.sample?.rows ?? [],
        ...(kyc.sample ? { customerRecords: kyc.sample.rows, kycSourceName: kyc.sample.fileName } : {}),
        sourceName: alerts.sample?.fileName,
        ...(previewOnly ? { previewOnly: true, previewRows: PREVIEW_ROWS } : {}),
        ...(agentSelection ? { agentSelection } : {})
    });

    const runPreview = async () => {
        stopFollowing();
        setStage('previewing');
        setPreview(null);
        setRefusal(null);
        setError(null);
        try {
            const submission = await benchmarkService.submitSuppliedSample(
                validateFlowCode(workflowCode), newInteractionId(), payload(true)
            );
            if (!submission.referenceId) {
                throw new BenchmarkRequestError('The preview started but returned no reference to follow it by.', null);
            }

            const controller = new AbortController();
            streamRef.current = controller;
            let settled = false;
            const settle = (next: () => void) => {
                if (settled) return;
                settled = true;
                stopFollowing();
                next();
            };
            // The stream only carries events once they are written to the context
            // graph; without one, nothing arrives and this is the only signal.
            timerRef.current = setTimeout(() => settle(() => {
                setError("The preview did not report back. The run's progress is published through the context graph; if that is not reachable in this environment, previews cannot be shown.");
                setStage('upload');
            }), PREVIEW_WAIT_MS);

            followContextStream(submission.referenceId, {
                signal: controller.signal,
                onEvent: (event) => {
                    const outcome = loaderOutcome(event);
                    if (!outcome) return;
                    settle(() => {
                        if (outcome.refusal) {
                            setRefusal(outcome.refusal);
                            setStage('refused');
                        } else {
                            setPreview(outcome.preview);
                            setStage('preview');
                        }
                    });
                },
                onDone: () => settle(() => {
                    setError('The preview finished without reporting how the sample was read. Try again, and if it persists, check the flow run log.');
                    setStage('upload');
                })
            }).catch((err) => {
                if (controller.signal.aborted) return;
                settle(() => {
                    setError(err?.message || "The preview's progress could not be followed.");
                    setStage('upload');
                });
            });
        } catch (err) {
            setError(err instanceof BenchmarkRequestError ? err.message : 'The preview could not be started.');
            setStage('upload');
        }
    };

    /**
     * Submits the sample for a full run — no previewOnly — and hands it to the
     * editor to follow. A start that fails returns to whichever step it came from.
     */
    const runBenchmark = async () => {
        const returnTo = stage;
        setStage('starting');
        setError(null);
        const interactionId = newInteractionId();
        try {
            const submission = await benchmarkService.submitSuppliedSample(
                validateFlowCode(workflowCode), interactionId, payload(false)
            );
            onRunStarted({ interactionId: submission.interactionId, referenceId: submission.referenceId });
            onClose();
        } catch (err) {
            setError(err instanceof BenchmarkRequestError ? err.message : 'The benchmark could not be started.');
            setStage(returnTo);
        }
    };

    const guidance = refusal ? refusalGuidance(refusal) : null;
    const activeStep = stage === 'upload' ? 0 : 1;

    const fileBox = (label: string, hint: string, kind: 'alerts' | 'kyc', loaded: LoadedFile, check: SampleCheck | null) => (
        <Box sx={{ p: 2, borderRadius: '10px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page', 0.5) }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg-body') }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25, mb: 1.5 }}>{hint}</Typography>
            <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileOutlinedIcon />}
                sx={{ textTransform: 'none', color: ink('#C4B5FD'), borderColor: 'rgba(167, 139, 250, 0.6)', '&:hover': { borderColor: '#C4B5FD' } }}
            >
                {loaded.sample ? 'Choose another file' : 'Choose file'}
                <input
                    hidden
                    type="file"
                    accept={ACCEPTED_EXTENSIONS.join(',')}
                    onChange={(e) => {
                        loadFile(e.target.files?.[0], kind);
                        e.target.value = '';
                    }}
                />
            </Button>
            {loaded.sample && (
                <Box className="flex items-center gap-2" sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                        {loaded.sample.fileName} · {loaded.sample.rows.length.toLocaleString()} {kind === 'alerts' ? 'alerts' : 'customers'}
                    </Typography>
                    {kind === 'kyc' && (
                        <Button size="small" onClick={() => setKyc(EMPTY_FILE)} sx={{ textTransform: 'none', color: dsVar('fg-2'), minWidth: 0 }}>
                            Remove
                        </Button>
                    )}
                </Box>
            )}
            {loaded.error && <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), mt: 1 }}>{loaded.error}</Typography>}
            {check?.blocks.map((b) => (
                <Typography key={b} sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), mt: 1, lineHeight: 1.5 }}>✕ {b}</Typography>
            ))}
            {check?.warnings.map((w) => (
                <Typography key={w} sx={{ fontSize: '0.75rem', color: ink('#FBBF24'), mt: 0.75, lineHeight: 1.5 }}>! {w}</Typography>
            ))}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={stage === 'previewing' || stage === 'starting' ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { backgroundColor: dsVar('page'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`, borderRadius: '14px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${dsVar('line')}` }}>
                <Box>
                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: dsVar('fg') }}>Benchmark a sample</Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                        Score alerts you supply, rather than your institution's history, with this workflow.
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={stage === 'previewing' || stage === 'starting'} sx={{ color: dsVar('fg-2') }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: '20px !important' }}>
                <Stepper
                    activeStep={activeStep}
                    sx={{
                        mb: 3,
                        '& .MuiStepLabel-label': { color: dsVar('fg-muted'), fontSize: '0.8125rem' },
                        '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': { color: dsVar('fg-body') },
                        '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: dsVar('accent-fg') },
                        '& .MuiStepIcon-text': { fill: '#0A192F' }
                    }}
                >
                    {['Upload', 'Run'].map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                </Stepper>

                {error && (
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.35)', backgroundColor: 'rgba(248, 113, 113, 0.08)' }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
                    </Box>
                )}

                {stage === 'upload' && (
                    <Box className="flex flex-col gap-3">
                        {prerequisitesError && (
                            <Typography sx={{ fontSize: '0.8125rem', color: ink('#FBBF24') }}>{prerequisitesError}</Typography>
                        )}
                        {prerequisites && !prerequisites.tierMapping.approved && (
                            <Box sx={{ p: 1.5, borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.35)', backgroundColor: 'rgba(248, 113, 113, 0.08)' }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), lineHeight: 1.6 }}>
                                    Your organisation's alert tiers have not been mapped onto the platform's and approved, so any sample
                                    would be refused. An administrator must approve the tier mapping first.
                                </Typography>
                            </Box>
                        )}
                        {prerequisites && (prerequisites.pendingMappings.alerts + prerequisites.pendingMappings.kyc) > 0 && (
                            <Typography sx={{ fontSize: '0.8125rem', color: ink('#FBBF24') }}>
                                {prerequisites.pendingMappings.alerts + prerequisites.pendingMappings.kyc} column(s) from an earlier file are
                                waiting for a person to confirm them. You will be asked to if this file uses them.
                            </Typography>
                        )}

                        <Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fileBox('Alerts', 'Closed alerts with their outcomes: CSV, JSON or JSONL, one row per alert.', 'alerts', alerts, alertCheck)}
                            {fileBox('KYC (optional)', "One record per customer, keyed like the alerts' customer column, ideally with an as-of date.", 'kyc', kyc, kycCheck)}
                        </Box>

                        {/* Investigation and EDD workflows: which agents the replay runs. */}
                        {workflowCode && (
                            <Box sx={{ p: 1.5, borderRadius: '8px', border: `1px solid ${dsVar('line')}` }}>
                                <AgentSelectionPicker
                                    flowCode={validateFlowCode(workflowCode)}
                                    value={agentSelection}
                                    onChange={setAgentSelection}
                                />
                            </Box>
                        )}

                        <Box className="flex flex-wrap items-center gap-3">
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>Templates:</Typography>
                            <Button size="small" href="assets/benchmark-templates/alerts-template.csv" download startIcon={<FileDownloadOutlinedIcon />} sx={{ textTransform: 'none', color: dsVar('fg-2') }}>
                                Alerts CSV
                            </Button>
                            <Button size="small" href="assets/benchmark-templates/kyc-template.csv" download startIcon={<FileDownloadOutlinedIcon />} sx={{ textTransform: 'none', color: dsVar('fg-2') }}>
                                KYC CSV
                            </Button>
                        </Box>
                    </Box>
                )}

                {(stage === 'previewing' || stage === 'starting') && (
                    <Box className="flex flex-col items-center gap-2" sx={{ py: 6 }}>
                        <CircularProgress size={28} sx={{ color: dsVar('accent-fg') }} />
                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>
                            {stage === 'previewing' ? 'Reading the sample as the benchmark will…' : 'Starting the benchmark…'}
                        </Typography>
                    </Box>
                )}

                {stage === 'preview' && preview && <SamplePreview preview={preview} />}

                {stage === 'refused' && refusal && (
                    <Box className="flex flex-col gap-2">
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: ink('#FCA5A5') }}>The sample was refused</Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), fontFamily: '"JetBrains Mono", monospace', lineHeight: 1.6, p: 1.5, borderRadius: '8px', backgroundColor: dsVar('page', 0.6), border: `1px solid ${dsVar('line')}`, wordBreak: 'break-word' }}>
                            {refusal}
                        </Typography>
                        {guidance && <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), lineHeight: 1.6 }}>{guidance.advice}</Typography>}
                    </Box>
                )}

                {stage === 'confirm' && (
                    <ConfirmColumns
                        relation={confirmRelation}
                        onConfirmed={runPreview}
                        onCancel={() => setStage('refused')}
                    />
                )}
            </DialogContent>

            {stage !== 'confirm' && stage !== 'previewing' && stage !== 'starting' && (
                <DialogActions sx={{ borderTop: `1px solid ${dsVar('line')}`, px: 3, py: 2 }}>
                    {stage !== 'upload' && (
                        <Button onClick={() => setStage('upload')} sx={{ textTransform: 'none', color: dsVar('fg-2') }}>
                            Back to files
                        </Button>
                    )}
                    {stage === 'upload' && (
                        <Button
                            onClick={runBenchmark}
                            disabled={blocked}
                            variant="contained"
                            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' }, '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') } }}
                        >
                            Test sample
                        </Button>
                    )}
                    {stage === 'refused' && guidance?.kind === 'drafts' && (
                        <Button
                            onClick={() => { setConfirmRelation(draftRelation(refusal)); setStage('confirm'); }}
                            variant="contained"
                            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' } }}
                        >
                            Confirm columns
                        </Button>
                    )}
                    {stage === 'preview' && (
                        <Button
                            onClick={runBenchmark}
                            variant="contained"
                            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' } }}
                        >
                            Run the benchmark
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SuppliedSampleWizard;
